import { useEffect, useState } from "react";
import API from "../../lib/api";
import { Badge } from "../ui/badge";
import { SegmentBar } from "./PublicStatusParts";

const COLOR = { up: "bg-emerald-500", degraded: "bg-amber-400", down: "bg-red-500", nodata: "bg-muted-foreground/20" };
const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const dur = (m) => (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`);
const pct = (v) => (v == null ? "—" : `${v}%`);

function CheckRow({ c }) {
  return (
    <div className="py-3 border-b border-border last:border-0" data-testid={`uptime-check-${c.key}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`h-2.5 w-2.5 rounded-full ${c.current === "up" ? "bg-emerald-500" : c.current === "down" ? "bg-red-500" : "bg-muted-foreground/40"}`} />
          <span className="text-sm font-semibold">{c.label}</span>
          <Badge variant="outline" className="text-[9px]" data-testid={`uptime-current-${c.key}`}>{c.current === "up" ? "Operational" : c.current === "down" ? "Down" : "No data"}</Badge>
          <span className="text-[11px] text-muted-foreground truncate" title={c.hint}>{c.hint}</span>
        </div>
        <div className="text-xs text-muted-foreground tabular-nums" data-testid={`uptime-pct-${c.key}`}>
          <span className="font-semibold text-foreground">{pct(c.uptime_7d)}</span> 7 d · <span className="font-semibold text-foreground">{pct(c.uptime_24h)}</span> 24 h
        </div>
      </div>
      <div className="mt-2">
        <SegmentBar segments={c.segments} testId={`uptime-bar-${c.key}`} blockClass={COLOR}
                    title={(s) => `${fmt(s.t)} — ${s.status === "nodata" ? "no readings" : `${s.up} up · ${s.down} down`}`} />
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground"><span>7 days ago</span><span>now</span></div>
    </div>
  );
}

export function UptimeTimeline({ tick }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    API.get("/platform/system-status/uptime").then(({ data }) => alive && setData(data)).catch(() => {});
    return () => { alive = false; };
  }, [tick]);
  if (!data) return <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">Loading uptime…</div>;
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="uptime-timeline">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h3 className="text-sm font-semibold">Uptime — last {data.days} days</h3>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-emerald-500" /> up</span>
          <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-amber-400" /> partly down</span>
          <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-red-500" /> down</span>
          <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-muted-foreground/20" /> no readings</span>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mb-1">Each block is one hour. {data.minutes_with_data.toLocaleString()} of {data.minutes_in_range.toLocaleString()} minutes have readings (gaps = the app wasn't running or collecting).</p>
      {data.checks.map((c) => <CheckRow key={c.key} c={c} />)}
      <div className="mt-4" data-testid="uptime-incidents">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Incidents ({data.incidents.length})</h4>
        {data.incidents.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="uptime-no-incidents">No incidents in the last {data.days} days.</p> : (
          <ul className="space-y-1">
            {data.incidents.map((i) => (
              <li key={`${i.check}-${i.started_at}`} className="flex items-center justify-between gap-3 text-xs rounded-md border border-border px-3 py-1.5" data-testid="uptime-incident-row">
                <span className="flex items-center gap-2 min-w-0">
                  <span className={`h-2 w-2 rounded-full ${i.ongoing ? "bg-red-500 animate-pulse" : "bg-muted-foreground/50"}`} />
                  <span className="font-medium">{i.label}</span>
                  <span className="text-muted-foreground">{i.ongoing ? "down since" : "down from"} {fmt(i.started_at)}{i.ended_at ? ` to ${fmt(i.ended_at)}` : ""}</span>
                </span>
                <span className="tabular-nums font-semibold shrink-0">{i.ongoing ? `ongoing · ${dur(i.duration_minutes)}` : dur(i.duration_minutes)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
